// frontend/admin-portal/src/components/products/ProductFilters.jsx
import React, { useMemo } from "react";
import { Input, SearchSelect } from "@fiberhome/shared-ui";

const KIND_LABELS = {
  passive: "Passive",
  active: "Active",
  cable: "Cable",
  instrument: "Instrument",
  accessory: "Accessory",
  device: "Device",
  hardware: "Hardware",
  sfp: "SFP / Transceivers",
  splitter: "Splitters",
  odf: "ODF / Patch Panels",
  spliceProtector: "Splice Protectors",
  cleaner: "Cleaners",
  puller: "Pullers",
  tool: "Tools",
  toolKit: "Tool Kits",
};

const KIND_ORDER = [
  "passive",
  "active",
  "cable",
  "sfp",
  "splitter",
  "odf",
  "instrument",
  "tool",
  "toolKit",
  "accessory",
  "spliceProtector",
  "cleaner",
  "puller",
  "hardware",
  "device",
];

export default function ProductFilters({
  filters,
  onChange,
  categories = [],
  brands = [],
}) {
  const set = (key, value) => onChange({ ...filters, [key]: value, page: 1 });

  /* Category options, grouped by kind so users can jump straight to
     "all cables" or "all tools" without scrolling through a flat list. */
  const categoryOptions = useMemo(() => {
    if (!categories.length) return [];

    const grouped = categories.reduce((acc, c) => {
      const kind = c.kind || "other";
      if (!acc[kind]) acc[kind] = [];
      acc[kind].push(c);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort(([a], [b]) => {
        const ai = KIND_ORDER.indexOf(a);
        const bi = KIND_ORDER.indexOf(b);
        return (ai < 0 ? 999 : ai) - (bi < 0 ? 999 : bi);
      })
      .map(([kind, list]) => ({
        group: KIND_LABELS[kind] || kind,
        options: list
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((c) => ({ value: c.id, label: c.name, kind: c.kind })),
      }));
  }, [categories]);

  /* Brand options as a flat, alphabetical list. */
  const brandOptions = useMemo(
    () =>
      brands
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((b) => ({ value: b.id, label: b.name })),
    [brands],
  );

  /* Kind is a small fixed list — keeping it as grouped options means
     the default "All Types" behaves the same as before, but the user
     can search "cab" → Cable. */
  const kindOptions = useMemo(
    () => [
      { value: "passive", label: "Passive" },
      { value: "cable", label: "Cable" },
      { value: "active", label: "Active" },
      { value: "instrument", label: "Instrument" },
      { value: "accessory", label: "Accessory" },
    ],
    [],
  );

  const stockOptions = useMemo(
    () => [{ value: "true", label: "Low Stock Only" }],
    [],
  );

  return (
    <div className="fh-product-filters">
      <Input
        placeholder="Search by name, SKU, or barcode..."
        value={filters.search || ""}
        onChange={(e) => set("search", e.target.value)}
      />

      <SearchSelect
        value={filters.categoryId || null}
        onChange={(v) => set("categoryId", v ?? "")}
        options={categoryOptions}
        placeholder="All Categories"
        searchPlaceholder="Search categories…"
        emptyMessage="No categories match your search"
        clearable
      />

      <SearchSelect
        value={filters.brandId || null}
        onChange={(v) => set("brandId", v ?? "")}
        options={brandOptions}
        placeholder="All Brands"
        searchPlaceholder="Search brands…"
        emptyMessage="No brands match your search"
        clearable
      />

      <SearchSelect
        value={filters.kind || null}
        onChange={(v) => set("kind", v ?? "")}
        options={kindOptions}
        placeholder="All Types"
        searchPlaceholder="Search types…"
        emptyMessage="No types match your search"
        clearable
      />

      <SearchSelect
        value={filters.lowStock || null}
        onChange={(v) => set("lowStock", v ?? "")}
        options={stockOptions}
        placeholder="All Stock"
        searchPlaceholder="Search stock status…"
        emptyMessage="No stock status matches"
        clearable
      />
    </div>
  );
}
